<template>
	<div class="chat-page">
		<!-- Key 验证弹窗 -->
		<div class="key-modal-overlay" v-if="showKeyModal">
			<div class="key-modal">
				<div class="key-modal-header">
					<h3>请输入访问密钥</h3>
					<p class="key-modal-desc">首次使用请输入您的访问密钥进行验证</p>
				</div>
				<div class="key-modal-body">
					<input class="key-input" v-model="keyInput" placeholder="请输入访问密钥" type="text"
						@keyup.enter="confirmKey" :disabled="keyValidating" />
					<p class="key-modal-error" v-if="keyError">{{ keyError }}</p>
				</div>
				<div class="key-modal-footer">
					<button class="key-btn key-btn-secondary" @click="$router.push('/')">返回首页</button>
					<button class="key-btn key-btn-primary" @click="confirmKey" :disabled="keyValidating">
						{{ keyValidating ? '验证中...' : '确认' }}
					</button>
				</div>
			</div>
		</div>
		<!-- 页面头部 -->
		<ChatHeader :title="currentTool.title" :tool-type="currentTool.type" @back="$router.push('/')"
			@new-chat="startNewChat" />

		<div class="chat-container">
			<!-- 侧边栏（仅 chat 类型显示） -->
			<ChatSidebar v-if="currentTool.type === 'chat'" :conversations="conversations"
				:current-conv-index="currentConvIndex" :sidebar-open="sidebarOpen"
				@switch-conversation="switchConversation" />
			<div class="sidebar-overlay" v-if="sidebarOpen" @click="sidebarOpen = false"></div>

			<!-- 主内容区 -->
			<main class="chat-main" :class="{ 'form-layout': currentTool.type === 'form' }">
				<!-- 移动端侧边栏切换按钮 -->
				<button class="sidebar-toggle" @click="sidebarOpen = !sidebarOpen" v-if="currentTool.type === 'chat'">
					<span>☰</span>
				</button>

				<!-- 表单式布局 -->
				<FormWorkspace v-if="currentTool.type === 'form'" :current-tool="currentTool" :form-data="formData"
					:is-submitting="isSubmitting" :progress-visible="progressVisible" :progress-percent="progressPercent"
					:result-duration="resultDuration" :result-step-text="resultStepText" :show-result-page="showResultPage"
					:result-title="resultTitle" :result-tool-id="resultToolName" :result-elapsed-ms="resultElapsed"
					:result-markdown="resultMarkdown" :result-copy-text="resultCopyText"
					:result-docx-base64="resultDocxBase64" :result-docx-filename="resultDocxFilename"
					:result-summary="resultSummary"
					:result-error-detail="resultErrorDetail"
					@submit="handleFormSubmit" @file-upload="handleFileUpload"
					@form-update="formData = $event" @download-template="downloadTemplate" />

				<!-- 学术绘图（academic-drawing）客户端渲染结果 -->
				<div v-if="(currentTool.taskId === 8 || currentTool.specialType === 'chart') && chartResult" class="chart-result-area">
					<!-- 顶部标题栏 -->
					<div class="chart-top-bar">
						<span class="chart-top-bar-title">运行结果</span>
						<span class="chart-top-bar-meta" v-if="chartElapsedMs">模板绘图 · {{ formatDuration(chartElapsedMs) }}</span>
					</div>
					<!-- 内容卡片 -->
					<div class="chart-content-card">
						<div class="chart-result-body" ref="chartResultBody" v-html="chartResult.html"></div>
					</div>
				</div>

				<!-- 学术绘图错误显示 -->
				<div v-if="(currentTool.taskId === 8 || currentTool.specialType === 'chart') && chartError" class="chart-error-card">
					<div class="chart-error-title">绘图失败</div>
					<div class="chart-error-detail">{{ chartError }}</div>
					<button class="chart-btn chart-btn-primary" @click="clearChartResult">重新输入</button>
				</div>

				<!-- 任务生成中遮罩（已废弃：进度由 result-card 展示，不再使用遮罩） -->

				<!-- 对话式布局 -->
				<ChatWorkspace v-if="currentTool.type === 'chat'" :current-tool="currentTool" :messages="messages"
					:is-loading="isLoading" :user-input="userInput" @send-message="sendMessage"
					@quick-action="sendQuickMessage" @input-change="userInput = $event"
					@scroll-bottom="scrollToBottom" />

				<!-- 特殊布局：学术自查 -->
				<SelfcheckWorkspace v-if="currentTool.type === 'special' && currentTool.templateType === 'selfcheck'"
					:current-tool="currentTool" :is-submitting="isSubmitting" :file-name="selfcheckFileName"
					@submit="submitSelfcheck" @file-upload="handleSelfcheckFileUpload"
					@download-template="downloadTemplate" />

				<!-- 特殊布局：论文结构化解析 -->
				<StructureWorkspace v-if="currentTool.type === 'special' && currentTool.specialType === 'structure'"
					:current-tool="currentTool" :is-submitting="isSubmitting" :structure-form="structureForm"
					:file-name="structureFileName" @submit="submitStructure" @form-change="structureForm = $event"
					@file-upload="handleStructureFileUpload" />

				<!-- 特殊布局：论文降 AI 率 -->
			</main>
		</div>
	</div>
</template>

<script>
	import ChatHeader from '@/components/ChatHeader.vue'
	import ChatSidebar from '@/components/ChatSidebar.vue'
	import FormWorkspace from '@/components/FormWorkspace.vue'
	import ChatWorkspace from '@/components/ChatWorkspace.vue'
	import SelfcheckWorkspace from '@/components/SelfcheckWorkspace.vue'
	import StructureWorkspace from '@/components/StructureWorkspace.vue'

	export default {
		name: 'Chat',
		components: {
			ChatHeader,
			ChatSidebar,
			FormWorkspace,
			ChatWorkspace,
			SelfcheckWorkspace,
			StructureWorkspace,
		},
		data() {
			return {
				// 通用状态
				sidebarOpen: false,
				showKeyModal: false,
				resultMarkdown: '',
				resultTitle: '',
				resultToolName: '',
				resultElapsed: 0,
				resultDocxBase64: '',
				resultDocxFilename: '',
				resultCopyText: '',
			resultSummary: {},
				resultErrorDetail: '',

				// 学术绘图（academic-drawing）客户端渲染结果
				chartResult: null,
				chartError: '',
				chartElapsedMs: 0,

				keyInput: '',
				keyValidating: false,
				keyError: '',
				isLoading: false,
				isSubmitting: false,
				pollTimer: null,

				// 运行结果卡片状态
				progressVisible: false,
				progressPercent: 0,
				resultDuration: '',
				resultStepText: '',
				showResultPage: false,

				// 对话相关
				currentConvIndex: 0,
				conversations: [{
					title: '新的对话',
					time: '刚刚',
					messages: []
				}],
				messages: [],
				userInput: '',

				// 表单相关
				formData: {},

				// 学术自查相关
				selfcheckFileName: '',

				// 论文结构化解析相关
				structureFileName: '',
				structureForm: {
					focus: '',
					outputLang: 'zh'
				},

				// 工具配置（taskId 映射表，对应 crt_task 表）
				taskIdMap: {
					'claritone-pubmed': 11,
					'claritone-reader': 2,
					'writing': 3,
					'fund-writing': 4,
					'paper-writing': 5,
					'paper-reduce-ai': 6,
					'english-polish': 7,
					'academic-drawing': 8,
					'smart-review': 9,
					'academic-selfcheck': 10
				},
				toolIdMap: {
					'claritone-pubmed': 'nnscholar_pubmed',
					'claritone-reader': 'paper_qa',
					'writing': 'survey',
					'fund-writing': 'grant_writer',
					'paper-writing': 'article_writer',
					'paper-reduce-ai': 'paper_humanizer',
					'english-polish': 'english_academic_polish',
					'smart-review': 'peer_review',
					'academic-selfcheck': 'integrity_audit'
				},
				// 进度条配置表：按工具设置总时长 + 阶段文案
				// 参考：claritone-reader 交互较轻（30 秒、3 阶段）；claritone-pubmed 检索/分析（300 秒、4 阶段）
				progressConfigMap: {
					'claritone-reader': {
						totalSec: 30,
						steps: [
							{ untilSec: 5,  text: '正在理解任务' },
							{ untilSec: 20, text: '正在分析问题' },
							{ untilSec: 30, text: '正在整理答案' },
							{ untilSec: Infinity, text: '即将完成' }
						]
					},
					'claritone-pubmed': {
						totalSec: 300,
						steps: [
							{ untilSec: 30,  text: '正在解析检索主题' },
							{ untilSec: 120, text: '正在检索文献数据库' },
							{ untilSec: 240, text: '正在分析与筛选结果' },
							{ untilSec: 300, text: '正在生成检索报告' },
							{ untilSec: Infinity, text: '即将完成' }
						]
					},
					'fund-writing': {
						totalSec: 300,
						steps: [
							{ untilSec: 30,  text: '正在整理基金材料' },
							{ untilSec: 120, text: '正在梳理立项依据与研究背景' },
							{ untilSec: 210, text: '正在撰写研究内容与创新点' },
							{ untilSec: 270, text: '正在整理年度计划与预期成果' },
							{ untilSec: 300, text: '正在生成 Word 正文稿' },
							{ untilSec: Infinity, text: '即将完成' }
						]
					},
					// 【2026-06-18 改造】SCI 论著工作台：5 分钟、5 阶段（参考 loading5.png 实际渲染）
					//   阶段1 = 整理论文材料；阶段2-4 = 撰写IMRAD各章节；阶段5 = 生成 Word 正文稿
					'paper-writing': {
						totalSec: 300,
						steps: [
							{ untilSec: 30,  text: '正在整理论文材料' },
							{ untilSec: 120, text: '正在梳理研究背景与研究目标' },
							{ untilSec: 210, text: '正在撰写IMRAD各章节正文' },
							{ untilSec: 270, text: '正在整理统计方法与结果说明' },
							{ untilSec: 300, text: '正在生成 Word 论文正文稿' },
							{ untilSec: Infinity, text: '即将完成' }
						]
					},
					// 【2026-06-18 新增】英文论文表达精修：2 分钟、4 阶段（读取→分析→精修→生成）
					'english-polish': {
						totalSec: 120,
						steps: [
							{ untilSec: 15,  text: '正在读取论文文本' },
							{ untilSec: 50,  text: '正在分析语法与表达问题' },
							{ untilSec: 90,  text: '正在逐句精修润色' },
							{ untilSec: 120, text: '正在生成精修后正文稿' },
							{ untilSec: Infinity, text: '即将完成' }
						]
					},
					// 【2026-06-18 新增】智能审稿（mart-review）进度条，参考 loading8.png（3阶段）
					'smart-review': {
						totalSec: 180,
						steps: [
							{ untilSec: 30,  text: '正在理解任务' },
							{ untilSec: 120, text: '正在分析稿件内容与审稿要求' },
							{ untilSec: 180, text: '正在生成审稿意见报告' },
							{ untilSec: Infinity, text: '即将完成' }
						]
					},
					// 学术绘图（academic-drawing）客户端渲染进度条：10秒/3阶段
					// 客户端 buildSmartChart 实际只跑几百毫秒，进度条用于过渡显示
					'academic-drawing': {
						totalSec: 10,
						steps: [
							{ untilSec: 3,  text: '正在解析粘贴数据' },
							{ untilSec: 8,  text: '正在渲染图表' },
							{ untilSec: 10, text: '正在生成图表' },
							{ untilSec: Infinity, text: '即将完成' }
						]
					},
					'__default__': {
						totalSec: 60,
						steps: [
							{ untilSec: 10, text: '正在整理输入内容和运行参数' },
							{ untilSec: 40, text: '正在运行' },
							{ untilSec: 60, text: '正在生成结果' },
							{ untilSec: Infinity, text: '即将完成' }
						]
					},
					// 【2026-06-18】writing 工具专属进度条（综述写作）：5 分钟，5 阶段
					'writing': {
						totalSec: 300,
						steps: [
							{ untilSec: 15,  text: '正在理解综述主题' },
							{ untilSec: 90,  text: '正在检索相关文献' },
							{ untilSec: 180, text: '正在筛选与归纳证据' },
							{ untilSec: 270, text: '正在撰写综述正文' },
							{ untilSec: 300, text: '正在整理参考文献' },
							{ untilSec: Infinity, text: '即将完成' }
						]
					},
					// 【2026-06-18 新增】论文降 AI 率：2 分钟、4 阶段（读取→逐段重写→校验→生成）
					'paper-reduce-ai': {
						totalSec: 120,
						steps: [
							{ untilSec: 10,  text: '正在读取并解析论文正文' },
							{ untilSec: 60,  text: '正在逐段降消 AI 痕迹' },
							{ untilSec: 100, text: '正在校验关键信息完整性' },
							{ untilSec: 120, text: '正在生成降 AI 后正文稿' },
							{ untilSec: Infinity, text: '即将完成' }
						]
					},
					// 【2026-06-18 新增】学术自查：3 分钟、4 阶段（解析数据表→逐行扫描→整理报告→即将完成）
					//   阶段 1 文字与 loading9.png 完全对齐："正在解析数据表" / "正在读取 CSV、TSV 或 XLSX 中的可复核数值。"
					'academic-selfcheck': {
						totalSec: 180,
						steps: [
							{ untilSec: 30,  text: '正在解析数据表' },
							{ untilSec: 120, text: '正在逐行扫描可疑数值与异常模式' },
							{ untilSec: 180, text: '正在整理学术自查报告' },
							{ untilSec: Infinity, text: '即将完成' }
						]
					}
				},
				toolConfigs: {
					'paper-writing': {
						title: 'SCI论著工作台',
						icon: '📝',
						type: 'form',
						subtitle: '依据IMRAD结构和研究类型报告规范，整理材料并生成可继续打磨的论文正文。',
						btnText: '立即提交',
						btnColor: 'linear-gradient(135deg, #2d5016 0%, #4a7c23 100%)',
						footerNote:'', //'论文正文可选中文或英文；主题限 1000 字，粘贴材料限 12000 字，DOCX 限 15MB，不生成任何图像或图注。',
						// 【2026-06-18 改造】SCI 论著的输出是纯 markdown，复用 ReaderResultPage 简洁结果页（与 fund-writing/writing 一致）
						//   根因：论著的 markdown 没有统计卡/文献表/综合分析等结构化数据，用 ResultPage 会显示空白占位
						//   'simple' = "纯 markdown 简洁输出"通用类型
						resultPageType: 'simple',
						// 【2026-06-18 新增】标题卡配置（参考 loading5.png 实际渲染）
						//   启用后，ReaderResultPage 会在顶部显示"SCI论著正文稿 / 中文 / 英文 · Word 导出" + 复制/下载按钮
						showTitleCard: true,
						titleCardTitle: 'SCI论著正文稿',
						titleCardMeta: '中文 / 英文 · Word 导出',
						// 【2026-06-18】进度条固定描述 + 底注（参考 loading5.png）
						progressDescText: '正在读取主题、研究材料和 Word 解析文本。',
						progressFooterText: 'Word 上传仅支持 DOCX，文件需控制在 15MB 以内。',
						formFields: [{
								key: 'primary_text',
								label: '论文主题或研究描述',
								type: 'textarea',
								required: true,
								rows: 5,
								placeholder: '例如：多模态眼底影像联合临床指标预测青少年近视进展的回顾性队列研究。'
							},
							{
								key: 'secondary_text',
								label: '现有材料（可选）',
								type: 'textarea',
								required: false,
								rows: 8,
								placeholder: '可粘贴摘要、研究设计、方法、结果、统计分析、参考文献线索或投稿要求。建议控制在 12000 字以内。'
							},
							{
								key: 'study_type',
								label: '研究类型',
								type: 'select',
								required: true,
								options: [{
									value: 'original',
									label: '原始研究'
								}, {
									value: 'clinical',
									label: '临床研究'
								}, {
									value: 'observational',
									label: '观察性研究'
								}, {
									value: 'trial',
									label: '随机对照试验'
								}, {
									value: 'diagnostic',
									label: '诊断 / 预测模型'
								}, {
									value: 'review',
									label: '综述'
								}, {
									value: 'methods',
									label: '方法学论文'
								}]
							},
							{
								key: 'citation_style',
								label: '引用格式',
								type: 'select',
								required: true,
								options: [{
									value: 'vancouver',
									label: 'Vancouver'
								}, {
									value: 'ama',
									label: 'AMA'
								}, {
									value: 'apa',
									label: 'APA'
								}, {
									value: 'none',
									label: '待补充'
								}]
							},
							{
								key: 'target_journal',
								label: '目标期刊（可选）',
								type: 'text',
								required: false,
								placeholder: '例如：眼科 AI 方向 SCI 期刊'
							},
							{
								key: 'file',
								label: '上传 Word（可选，仅 DOCX，≤15MB，不含图片）',
								type: 'file',
								required: false,
								accept: '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
								uploadText: '选择文件',
								hint: '未选择任何文件'
							},
							{
								key: 'language',
								label: '输出语言',
								type: 'select',
								required: true,
								options: [{
									value: 'zh',
									label: '中文'
								}, {
									value: 'en',
									label: '英文'
								}]
							}
						]
					},
					'fund-writing': {
						title: '基金申请书工作台',
						icon: '💼',
						type: 'form',
						subtitle: '生成中文基金申请书正文稿，覆盖立项依据、研究内容、创新点、年度计划和预期成果',
						btnText: '立即提交',
						btnColor: 'linear-gradient(135deg, #2d5016 0%, #4a7c23 100%)',
						footerNote:'',// '只生成中文基金正文；主题限 1000 字，粘贴文字材料限 10000 字，Word 上传仅支持 .docx，文件需控制在 15MB 以内。',
						// 【2026-06-18 改造】基金申请的输出是纯 markdown，复用 ReaderResultPage 简洁结果页
						//   根因：基金申请的 markdown 没有统计卡/文献表/综合分析等结构化数据，用 ResultPage 会显示空白占位
						//   'simple' = "纯 markdown 简洁输出"通用类型（与 claritone-reader / writing 共享）
						resultPageType: 'simple',
						// 【2026-06-18 新增】标题卡配置（参考 result4.png 实际渲染）
						//   启用后，ReaderResultPage 会在顶部显示"基金申请书正文稿 / 中文 · 宋体 Word 导出" + 复制/下载按钮
						showTitleCard: true,
						titleCardTitle: '基金申请书正文稿',
						titleCardMeta: '中文 · 宋体 Word 导出',
						// 【2026-06-18】进度条固定描述 + 底注（参考 loading4.png）
						progressDescText: '正在读取主题、文字材料和 Word 解析文本。',
						progressFooterText: '基金写作只生成中文正文稿；Word 上传仅支持 DOCX，文件需控制在 15MB 以内。',
						formFields: [{
								key: 'primary_text',
								label: '项目主题或研究描述',
								type: 'textarea',
								required: false,
								rows: 5,
								placeholder: '例如：基于多模态眼底影像和临床指标的青少年近视进展风险预测研究。'
							},
							{
								key: 'secondary_text',
								label: '文字材料（可选）',
								type: 'textarea',
								required: false,
								rows: 8,
								placeholder: '可粘贴研究基础、前期结果、技术路线、代表性成果或参考文献线索。建议控制在 10000 字以内。'
							},
							{
								key: 'file',
								label: '上传 Word 文档（仅支持 .docx）',
								type: 'file',
								required: true,
								accept: '.docx',
								uploadText: '选择 .docx 文件',
								hint: '未选择任何文件'
							}
						]
					},
					'smart-review': {
						title: '智能审稿',
						icon: '🔍',
						type: 'form',
						subtitle: '按高水平期刊标准输出主要问题、实验缺口和可执行修改路线',
						btnText: '立即提交',
						btnColor: 'linear-gradient(135deg, #2d5016 0%, #4a7c23 100%)',
						fieldGroups: {
							row1: {
								cols: 2
							}
						},
						formFields: [{
								key: 'primary_text',
								label: '学科领域与审稿关注点',
								type: 'textarea',
								required: false,
								rows: 4,
								maxlength: 800,
								placeholder: '例如：医学人工智能；重点关注外部验证、统计分析和临床价值。',
								desc: '限 800 字'
							},
							{
								key: 'file',
								label: '上传稿件 PDF',
								type: 'file',
								required: true,
								accept: '.pdf,application/pdf',
								uploadText: '选择文件',
								hint: '仅支持 PDF 格式'
							},
							{
								key: 'language',
								group: 'row1',
								label: '输出语言',
								type: 'select',
								required: false,
								options: [{
										value: 'zh',
										label: '中文'
									},
									{
										value: 'en',
										label: '英文'
									}
								]
							},
							{
								key: 'review_depth',
								group: 'row1',
								label: '审稿深度',
								type: 'select',
								required: false,
								options: [{
										value: 'standard',
										label: '标准审稿'
									},
									{
										value: 'deep',
										label: '深度审稿'
									}
								]
							}
						],
					// 【2026-06-18 新增】简洁结果页（纯内容输出，无复制/下载按钮栏）
					resultPageType: 'simple',
					showTitleCard: false,
					titleCardTitle: '',
					titleCardMeta: '',
					progressDescText: '正在理解任务',
					progressFooterText: '正在整理输入内容和运行参数。\n任务正在处理中，请保持本页打开。'
					},
					'writing': {
						title: '综述写作工作台',
						icon: '✍️',
						type: 'form',
						subtitle: '帮助您撰写高质量的学术综述',
						btnText: '立即提交',
						btnColor: 'linear-gradient(135deg, #2d5016 0%, #4a7c23 100%)',
						// 【2026-06-18 改造】综述写作的输出是纯 markdown（与 paper_qa 类似），复用 ReaderResultPage 简洁结果页
						//   根因：综述写作的 markdown 没有统计卡/文献表/综合分析等结构化数据，用 ResultPage 会显示空白占位
						//   'simple' = "纯 markdown 简洁输出"通用类型（与 claritone-reader 共享）
						resultPageType: 'simple',
						// 【2026-06-18】进度条固定描述 + 底注（参考 loading3.png）
						progressDescText: '正在将主题整理为适合检索和综述写作的科研问题。',
						progressFooterText: '综述写作需要完成检索、筛选和长文生成，通常需要几分钟，请保持本页打开。',
						formFields: [{
								key: 'primary_text',
								label: '综述主题',
								type: 'textarea',
								required: true,
								rows: 4,
								placeholder: '例如：多模态眼科模型预测轴长进展的临床证据与方法谱系。'
							},
							{
								key: 'language',
								label: '输出语言',
								type: 'select',
								required: true,
								options: [{
									value: 'zh',
									label: '中文'
								}, {
									value: 'en',
									label: '英文'
								}]
							}
						]
					},
					'paper-translation': {
						title: '论文翻译',
						icon: '🌐',
						type: 'form',
						subtitle: '专业的学术论文翻译服务',
						btnText: '开始翻译',
						formFields: [{
								key: 'sourceLang',
								label: '源语言',
								type: 'select',
								required: true,
								options: [{
									value: 'zh',
									label: '中文'
								}, {
									value: 'en',
									label: '英文'
								}, {
									value: 'ja',
									label: '日文'
								}, {
									value: 'de',
									label: '德文'
								}]
							},
							{
								key: 'targetLang',
								label: '目标语言',
								type: 'select',
								required: true,
								options: [{
									value: 'en',
									label: '英文'
								}, {
									value: 'zh',
									label: '中文'
								}, {
									value: 'ja',
									label: '日文'
								}, {
									value: 'de',
									label: '德文'
								}]
							},
							{
								key: 'document',
								label: '上传文档',
								type: 'file',
								required: true,
								accept: '.docx,.pdf,.txt',
								uploadText: '选择文件'
							},
							{
								key: 'domain',
								label: '专业领域',
								type: 'select',
								required: true,
								options: [{
									value: 'general',
									label: '通用'
								}, {
									value: 'medicine',
									label: '医学'
								}, {
									value: 'engineering',
									label: '工程'
								}, {
									value: 'science',
									label: '理学'
								}]
							},
							{
								key: 'requirements',
								label: '翻译要求',
								type: 'textarea',
								required: false,
								rows: 4,
								placeholder: '请输入特殊的翻译要求，如：术语偏好、格式要求、风格要求等...'
							}
						]
					},
					'academic-qa': {
						title: '学术快答',
						icon: '💡',
						type: 'chat',
						welcomeTitle: '你好！我是学术快答助手',
						welcomeDesc: '快速回答您的学术问题，提供专业的学术指导和建议',
						quickActions: [
							'如何撰写高质量的文献综述？',
							'论文中如何正确引用文献？',
							'如何选择合适的学术期刊投稿？',
							'科研论文的写作技巧有哪些？'
						]
					},
					'literature-qa': {
						title: '引用文献真伪鉴别',
						icon: '',
						type: 'form',
						subtitle: '基于论文内容定位证据片段，给出答案、置信度和待验证缺口。',
						btnText: '开始运行',
						btnColor: '#1a3a2e',
						formFields: [{
								key: 'primary_text',
								label: '问题',
								type: 'textarea',
								required: true,
								rows: 4,
								placeholder: '例如：这篇论文的核心创新是否足以支撑一个新课题？'
							},
							{
								key: 'file',
								label: '上传论文 PDF',
								type: 'file',
								required: false,
								accept: '.pdf,application/pdf',
								uploadText: '选择文件',
								hint: '未选择任何文件'
							},
							{
								key: 'language',
								label: '输出语言',
								type: 'select',
								required: true,
								options: [{
									value: 'zh',
									label: '中文'
								}, {
									value: 'en',
									label: '英文'
								}]
							}
						]
					},
					'academic-selfcheck': {
						title: '学术自查',
						icon: '✅',
						type: 'form',
						subtitle: '上传 CSV、TSV 或 XLSX 数据表，检查重复值、末位数字、固定关系和 Benford 适用性等异常线索。',
						btnText: '立即提交',
						btnColor: 'linear-gradient(135deg, #2d5016 0%, #4a7c23 100%)',
						// 【2026-06-18 改造】与 smart-review 一致：复用 ReaderResultPage 简洁结果页（'simple' = 纯 markdown 简洁输出）
						//   根因：学术自查的输出是纯 markdown，用 ResultPage 会显示空白占位
						resultPageType: 'simple',
						// 【2026-06-18 新增】隐藏标题卡（与 smart-review 一致；学术自查输出是数据表报告，无复制/下载按钮栏）
						showTitleCard: false,
						titleCardTitle: '',
						titleCardMeta: '',
						// 【2026-06-18 新增】进度条固定描述 + 底注（参考 loading9.png 实际渲染）
						//   阶段 1 文字 = "正在解析数据表" + "正在读取 CSV、TSV 或 XLSX 中的可复核数值。"
						//   底注 = 学术自查只处理 CSV/TSV/XLSX；不上传 PDF，不抓取链接，不做 OCR 或模型联网审计。
						progressDescText: '正在解析数据表',
						progressFooterText: '正在读取 CSV、TSV 或 XLSX 中的可复核数值。\n学术自查只处理 CSV、TSV、XLSX 数据表；不上传 PDF，不抓取链接，不做 OCR 或模型联网审计。',
						showTemplatePreview: true,
						templateType: 'table',
						templateTableTitle: '学术自查数据表',
						templateTableHint: 'CSV / TSV / XLSX',
						templateTableColumns: ['record_id', 'group', 'metric', 'value', 'unit', 'timepoint',
							'source_table', 'note'
						],
						templateTableRows: [
							['S001', 'Treatment', 'Axial length', '24.12', 'mm', 'baseline', 'Table 1', '原始测量'],
							['S002', 'Treatment', 'Axial length', '24.28', 'mm', 'baseline', 'Table 1', '原始测量'],
							['S003', 'Control', 'Axial length', '23.91', 'mm', 'baseline', 'Table 1', '原始测量'],
							['S004', 'Control', 'Axial length', '24.05', 'mm', 'baseline', 'Table 1', '原始测量']
						],
						templateActions: [{
								label: '下载 Excel 模板',
								format: 'tsv',
								url: '/demo/学术自查数据表模板.tsv'
							},
							{
								label: '下载 CSV 模板',
								format: 'csv',
								url: '/demo/学术自查数据表模板.csv'
							}
						],
						templateActionHint: '上传 CSV、TSV 或 XLSX 数据表。',
						formFields: [{
							key: 'file',
							label: '上传数据表',
							type: 'file',
							required: true,
							accept: '.csv,.tsv,.xlsx,text/csv,text/tab-separated-values,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
							uploadText: '选择文件',
							hint: '支持 CSV、TSV、XLSX 格式'
						}]
					},
					'academic-drawing': {
						title: '学术绘图',
						taskId: 8,
						icon: '📊',
						type: 'form',
						subtitle: '选择图表类型后粘贴数据，生成置信区间图、柱状图、折线图、散点图和饼图，支持 SVG 与 PNG 导出',
						btnText: '开始运行',
						btnColor: 'linear-gradient(135deg, #2d5016 0%, #4a7c23 100%)',
						showTemplatePreview: true,
						templateType: 'table',
						templateTableTitle: '置信区间图指定模板',
						templateTableHint: '只能使用这些列名和列顺序',
						templateTableColumns: ['结局', '队列', '模型', '数值', '下限', '上限'],
						templateTableRows: [
							['Outcome 1', 'Cohort A', 'Model A', '0.928', '0.897', '0.959'],
							['Outcome 1', 'Cohort A', 'Model B', '0.623', '0.505', '0.740'],
							['Outcome 1', 'Cohort A', 'Model C', '0.656', '0.527', '0.786'],
							['Outcome 1', 'Cohort B', 'Model A', '0.790', '0.612', '0.968'],
							['Outcome 1', 'Cohort B', 'Model B', '0.629', '0.561', '0.696'],
							['Outcome 2', 'Cohort A', 'Model A', '0.798', '0.713', '0.884'],
							['Outcome 2', 'Cohort A', 'Model B', '0.610', '0.505', '0.715']
						],
						fieldGroups: {
							row1: {
								cols: 2
							},
							row2: {
								cols: 2
							}
						},
						formFields: [{
								key: 'chart_type',
								group: 'row1',
								label: '图表类型',
								type: 'select',
								required: true,
								options: [{
										label: '置信区间图',
										value: 'forest_plot'
									},
									{
										label: '柱状图',
										value: 'bar_chart'
									},
									{
										label: '折线图',
										value: 'line_chart'
									},
									{
										label: '散点图',
										value: 'scatter_plot'
									},
									{
										label: '饼图',
										value: 'pie_chart'
									}
								],
								default: 'forest_plot'
							},
							{
								key: 'chart_title',
								group: 'row1',
								label: '图题（可选）',
								type: 'text',
								required: false,
								placeholder: '例如：模型性能比较'
							},
							{
								key: 'x_label',
								group: 'row2',
								label: '横轴标签（可选）',
								type: 'text',
								required: false,
								placeholder: '例如：队列 / 结局'
							},
							{
								key: 'y_label',
								group: 'row2',
								label: '纵轴 / 指标标签（可选）',
								type: 'text',
								required: false,
								placeholder: '例如：C-index'
							},
							{
								key: 'primary_text',
								label: 'Excel / CSV 表格粘贴区',
								type: 'textarea',
								required: true,
								rows: 10,
								placeholder: '结局 队列 模型 数值 下限 上限\nOutcome 1 Cohort A Model A 0.928 0.897 0.959\n...',
								desc: '支持当前模板、常规二维表和带 95% CI 的科研结果表。'
							}
						]
					},
					'english-polish': {
						title: '英文论文表达精修',
						icon: '✨',
						type: 'form',
						subtitle: '修正英文论文表达、语法和句式，使其更清晰、严谨、自然。选择 LaTeX 时会尽量保留命令、引用和公式；选择 Word 时输出纯净英文段落。',
						btnText: '立即提交',
						btnColor: 'linear-gradient(135deg, #2d5016 0%, #4a7c23 100%)',
						// 【2026-06-18 改造】进度条文字描述（参照 loading7.png）
						progressDescText: '正在读取论文文本',
						progressFooterText: '正在合并短文本输入和上传文档，并检查长度、格式和文件类型。',
						progressBottomNote: '短文本只处理一个完整段落；长正文请上传 DOCX、TXT、TEX 或 MD 文档，并按章节分批处理。',
						footerNote:'',// '短文本框只放一个完整段落（≤2500 字符）；全文或章节请上传文档。文档限 DOCX ≤1MB 或 TXT/TEX/MD ≤128KB；解析后正文限中文 25000 字或英文 8000 words，且总字符≤55000。',
												// 【2026-06-18 改造】输出复用 ReaderResultPage 简洁结果页（与 fund-writing 同款）
						resultPageType: 'simple',
						showTitleCard: true,
						titleCardTitle: '英文论文精修稿',
						titleCardMeta: 'English · Word/LaTeX 导出',
						formFields: [{
								key: 'primary_text',
								label: '英文论文正文',
								type: 'textarea',
								required: true,
								rows: 8,
								maxlength: 2500,
								placeholder: '请粘贴英文摘要、引言、方法、结果或讨论段落。系统会修正语法、冠词、句式和学术表达。'
							},
							{
								key: 'output_format',
								label: '输出格式',
								type: 'select',
								required: true,
								options: [{
									value: 'word',
									label: 'Word 纯文本输出'
								}, {
									value: 'latex',
									label: 'LaTeX 格式输出'
								}]
							},
							{
								key: 'file',
								label: '上传文档（可选，DOCX ≤1MB，TXT/TEX/MD ≤128KB）',
								type: 'file',
								required: false,
								accept: '.docx,.txt,.tex,.md,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
								uploadText: '选择文件',
								hint: '未选择任何文件'
							}
						]
					},
					'paper-reduce-ai': {
						title: '论文降 AI 率',
						icon: '🔄',
						type: 'form',
						subtitle: '仅支持英文论文正文，删除图片、表格、参考文献和注释后上传。',
						btnText: '立即提交',
						btnColor: 'linear-gradient(135deg, #2d5016 0%, #4a7c23 100%)',
						showTemplatePreview: true,
						templateContent: 'Multimodal retinal imaging for predicting adolescent myopia progression\n\nAbstract\n\nThis study aimed to evaluate whether multimodal retinal imaging combined with clinical indicators can improve prediction of adolescent myopia progression. The model used verified cohort data and reported performance metrics that should remain unchanged during editing.\n\nIntroduction\n\nMyopia progression in adolescents remains a major clinical and public health problem. Existing prediction studies often rely on limited clinical variables, which may not capture retinal structural changes related to disease progression.\n\nMethods\n\nWe conducted a retrospective cohort study using multimodal retinal images and clinical measurements. Model development and validation followed a prespecified analysis plan. All sample sizes, statistical results, abbreviations, and model names in this section must be preserved.\n\nResults',
						// 【2026-06-18 改造】论文降 AI 率输出是纯 markdown，复用 ReaderResultPage 简洁结果页
						//   根因：降 AI 率的结果没有统计卡/文献表/综合分析等结构化数据，用 ResultPage 会显示空白占位
						//   'simple' = "纯 markdown 简洁输出"通用类型（与 claritone-reader / writing / fund-writing 共享）
						resultPageType: 'simple',
						// 标题卡配置（参考 loading6.png / result6.png 实际渲染）
						//   启用后，ReaderResultPage 会在顶部显示"论文降 AI 率正文稿 / English · Word 导出" + 复制/下载按钮
						showTitleCard: true,
						titleCardTitle: '论文降 AI 率正文稿',
						titleCardMeta: 'English · Word 导出',
						// 进度条固定描述 + 底注（参考 loading6.png）
						progressDescText: '正在读取并解析上传的论文正文。',
						progressFooterText: '降 AI 率修改句式、用词和表达方式，保留所有数字、名称、专业缩写、参考文献和表格结果；不删减不添加实质结论和证据。',
						formFields: [{
							key: 'file',
							label: '上传英文论文正文（必填，DOCX ≤1MB，TXT ≤128KB）',
							type: 'file',
							required: true,
							accept: '.docx,.txt,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
							uploadText: '选择文件',
							hint: '未选择任何文件'
						}]
					},
					'paper-structure': {
						title: '论文结构化解析',
						icon: '📊',
						type: 'special',
						specialType: 'structure',
						subtitle: '提炼研究问题、方法路径、关键结论、局限和可复现实验线索。',
						btnText: '开始运行',
						btnColor: 'linear-gradient(135deg, #2d5016 0%, #4a7c23 100%)'
					},
					'claritone-pubmed': {
						title: 'PubMed 智能检索与分析',
						icon: '🧬',
						type: 'form',
						subtitle: '生成 PubMed 检索式，返回 IF/JCR/中科院分区表格，并输出综合分析报告',
						btnText: '立即提交',
						btnColor: 'linear-gradient(135deg, #2d5016 0%, #4a7c23 100%)',
						formFields: [{
								key: 'primary_text',
								label: '研究主题或检索关键词',
								type: 'textarea',
								required: true,
								rows: 4,
								maxlength: 2200,
								placeholder: '例如：青少年近视进展 眼轴；myopia progression axial length；multimodal ophthalmology model axial length progression'
							},
							{
								key: 'nnscholar_mode',
								label: '检索方式',
								type: 'select',
								required: true,
								options: [{
										value: 'ai',
										label: '智能生成'
									},
									{
										value: 'direct',
										label: '直接检索'
									}
								],
								group: 'search'
							},
							{
								key: 'nnscholar_max',
								label: '返回数量',
								type: 'select',
								required: true,
								options: [{
										value: '50',
										label: '50 篇'
									},
									{
										value: '30',
										label: '30 篇'
									},
									{
										value: '20',
										label: '20 篇'
									},
									{
										value: '10',
										label: '10 篇'
									}
								],
								group: 'search'
							},
							{
								key: 'nnscholar_sort',
								label: '排序方式',
								type: 'select',
								required: true,
								options: [{
										value: 'relevance',
										label: '相关性'
									},
									{
										value: 'date',
										label: '最新'
									}
								],
								group: 'search'
							},
							{
								key: 'nnscholar_source',
								label: '来源',
								type: 'select',
								required: true,
								options: [{
										value: 'balanced',
										label: '综合来源'
									},
									{
										value: 'formal',
										label: '正式文献优先'
									},
									{
										value: 'include_arxiv',
										label: '包含预印本'
									}
								],
								group: 'search'
							},
							{
								key: 'nnscholar_analysis',
								label: '分析',
								type: 'select',
								required: true,
								options: [{
										value: 'full',
										label: '综合分析'
									},
									{
										value: 'search_only',
										label: '仅表格'
									}
								],
								group: 'search'
							},
							{
								key: 'nnscholar_year_start',
								label: '发表年份-起始年',
								type: 'select',
								required: false,
								options: [{
										value: '2026',
										label: '2026'
									},
									{
										value: '2025',
										label: '2025'
									},
									{
										value: '2024',
										label: '2024'
									},
									{
										value: '2023',
										label: '2023'
									},
									{
										value: '2022',
										label: '2022'
									},
									{
										value: '2021',
										label: '2021'
									},
									{
										value: '2020',
										label: '2020'
									},
									{
										value: '2019',
										label: '2019'
									},
									{
										value: '2018',
										label: '2018'
									},
									{
										value: '2017',
										label: '2017'
									},
									{
										value: '2016',
										label: '2016'
									},
									{
										value: '2015',
										label: '2015'
									},
									{
										value: '2014',
										label: '2014'
									},
									{
										value: '2013',
										label: '2013'
									},
									{
										value: '2012',
										label: '2012'
									},
									{
										value: '2011',
										label: '2011'
									},
									{
										value: '2010',
										label: '2010'
									},
									{
										value: '2009',
										label: '2009'
									},
									{
										value: '2008',
										label: '2008'
									},
									{
										value: '2007',
										label: '2007'
									},
									{
										value: '2006',
										label: '2006'
									},
									{
										value: '2005',
										label: '2005'
									},
									{
										value: '2004',
										label: '2004'
									},
									{
										value: '2003',
										label: '2003'
									},
									{
										value: '2002',
										label: '2002'
									},
									{
										value: '2001',
										label: '2001'
									},
									{
										value: '2000',
										label: '2000'
									}
								],
								group: 'filter'
							},
							{
								key: 'nnscholar_year_end',
								label: '发表年份-结束年',
								type: 'select',
								required: false,
								options: [{
										value: '2026',
										label: '2026'
									},
									{
										value: '2025',
										label: '2025'
									},
									{
										value: '2024',
										label: '2024'
									},
									{
										value: '2023',
										label: '2023'
									},
									{
										value: '2022',
										label: '2022'
									},
									{
										value: '2021',
										label: '2021'
									},
									{
										value: '2020',
										label: '2020'
									},
									{
										value: '2019',
										label: '2019'
									},
									{
										value: '2018',
										label: '2018'
									},
									{
										value: '2017',
										label: '2017'
									},
									{
										value: '2016',
										label: '2016'
									},
									{
										value: '2015',
										label: '2015'
									},
									{
										value: '2014',
										label: '2014'
									},
									{
										value: '2013',
										label: '2013'
									},
									{
										value: '2012',
										label: '2012'
									},
									{
										value: '2011',
										label: '2011'
									},
									{
										value: '2010',
										label: '2010'
									},
									{
										value: '2009',
										label: '2009'
									},
									{
										value: '2008',
										label: '2008'
									},
									{
										value: '2007',
										label: '2007'
									},
									{
										value: '2006',
										label: '2006'
									},
									{
										value: '2005',
										label: '2005'
									},
									{
										value: '2004',
										label: '2004'
									},
									{
										value: '2003',
										label: '2003'
									},
									{
										value: '2002',
										label: '2002'
									},
									{
										value: '2001',
										label: '2001'
									},
									{
										value: '2000',
										label: '2000'
									}
								],
								group: 'filter'
							}
						],
						fieldGroups: {
							search: {
								title: '检索设置',
								cols: 5
							},
							filter: {
								title: '筛选条件',
								cols: 4
							}
						}
					},
					'claritone-reader': {
						title: '引用文献真伪鉴别',
						icon: '',
						type: 'form',
						subtitle: '基于论文内容定位证据片段，给出答案、置信度和待验证缺口。',
						btnText: '立即提交',
						btnColor: '#1a3a2e',
						// 【2026-06-18 改造】切换为简洁结果页 ReaderResultPage（无统计卡/文献表/综合分析）
						//   根因：paper_qa 响应只有 markdown 字段，原 ResultPage 的统计卡/数据源 chip/文献表/综合分析都不适用
						//   改名为 'simple'（"纯 markdown 简洁输出"通用类型），writing 工具也复用
						resultPageType: 'simple',
						formFields: [{
								key: 'primary_text',
								label: '问题',
								type: 'textarea',
								required: true,
								rows: 4,
								placeholder: '例如：这篇论文的核心创新是否足以支撑一个新课题？'
							},
							{
								key: 'file',
								label: '上传论文 PDF',
								type: 'file',
								required: false,
								accept: '.pdf,application/pdf',
								uploadText: '选择文件',
								hint: '未选择任何文件'
							},
							{
								key: 'language',
								label: '输出语言',
								type: 'select',
								required: true,
								options: [{
									value: 'zh',
									label: '中文'
								}, {
									value: 'en',
									label: '英文'
								}]
							}
						]
					}
				}
			}
		},
		computed: {
			currentTool() {
				const toolId = this.$route.query.tool || 'academic-qa'
				return this.toolConfigs[toolId] || this.toolConfigs['academic-qa']
			}
		},
		watch: {
			'$route.query.tool'() {
				this.messages = []
				this.formData = {}
				this.initFormData()
				// 切换模块后重新校验已存密钥的模块授权
				const savedKey = localStorage.getItem('accessKey')
				if (savedKey) {
					this.checkKeyPermission(savedKey)
				}
			}
		},
		mounted() {
			this.initFormData()
			this.$nextTick(() => {
				// 学术绘图（academic-drawing）客户端渲染，不走后端 API，无需密钥
				const __toolKey = this.$route.query.tool
				if (__toolKey === 'academic-drawing') {
					return
				}
				const savedKey = localStorage.getItem('accessKey')
				if (!savedKey) {
					this.showKeyModal = true
				} else {
					// 已存密钥也需校验当前模块授权（密钥有效但无本模块权限时提示）
					this.checkKeyPermission(savedKey)
				}
			})
		},
		methods: {
			// ========== 通用方法 ==========

			// 校验密钥是否授权当前功能模块；未授权弹密钥框提示，但不清除已存密钥（密钥本身可能仍有效）
			checkKeyPermission(key) {
				const __toolKey = this.$route.query.tool
				if (__toolKey === 'academic-drawing') return
				const taskId = this.$route.query.taskId
				if (!taskId) return
				this.$axios.post('/api/token/query', { token: key })
					.then(res => {
						if (res.data && res.data.status && res.data.status === 200 && res.data.data) {
							const ids = res.data.data.taskIds
							const allowed = ids && ids.split(',').map(s => s.trim()).indexOf(String(taskId)) !== -1
							if (!allowed) {
								this.keyError = '激活码权限不足，请联系客服'
								this.showKeyModal = true
							}
						}
					})
					.catch(() => {})
			},

			initFormData() {
				if (this.currentTool.formFields) {
					this.currentTool.formFields.forEach(field => {
						if (field.default !== undefined && field.default !== null) {
							this.$set(this.formData, field.key, field.default)
						} else if (field.type === 'select' && field.options && field.options.length > 0) {
							this.$set(this.formData, field.key, field.options[0].value)
						} else {
							this.$set(this.formData, field.key, '')
						}
					})
				}
			},

			confirmKey() {
				const key = this.keyInput.trim()
				if (!key) {
					this.keyError = '请输入访问密钥'
					return
				}
				this.keyValidating = true
				this.keyError = ''
				this.$axios.post('/api/token/query', {
						token: key
					})
					.then(res => {
						this.keyValidating = false
						if (res.data && res.data.status && res.data.status === 200 && res.data.data) {
							const tokenData = res.data.data
							// 验证 token 状态
							if (tokenData.status !== undefined && tokenData.status !== 1) {
								this.keyError = '密钥已失效'
								return
							}
							// 验证过期时间
							if (tokenData.expireTime) {
								const expireDate = new Date(tokenData.expireTime)
								if (expireDate < new Date()) {
									this.keyError = '密钥已过期'
									return
								}
							}
							// 验证当前功能模块授权（空=不可用）
							const taskId = this.$route.query.taskId
							if (taskId) {
								const ids = tokenData.taskIds
								const allowed = ids && ids.split(',').map(s => s.trim()).indexOf(String(taskId)) !== -1
								if (!allowed) {
									this.keyError = '激活码权限不足，请联系客服'
									return
								}
							}
							// 验证通过，保存到 localStorage
							localStorage.setItem('accessKey', key)
							this.showKeyModal = false
							this.keyInput = ''
						} else {
							this.keyError = (res.data && res.data.errMsg) || '密钥无效'
						}
					})
					.catch(err => {
						this.keyValidating = false
						this.keyError = '验证请求失败：' + (err.message || err)
					})
			},

			// ========== 表单式布局方法 ==========

			handleFormSubmit(formData) {
				// 学术绘图（academic-drawing）走客户端即时渲染，不走后端 API，无需密钥
				const __toolKey = this.$route.query.tool
				if (__toolKey === 'academic-drawing') {
					return this.handleAcademicDrawing(formData)
				}
				const savedKey = localStorage.getItem('accessKey')
				if (!savedKey) {
					alert('请先设置访问密钥')
					this.showKeyModal = true
					return
				}
				// 根据 tool 参数读取进度配置：阶段文案 + 总时长
				const toolKey = this.$route.query.tool || 'academic-qa'
				const progressCfg = this.progressConfigMap[toolKey] || this.progressConfigMap['__default__']
				this.isSubmitting = true
				// 重置结果卡片状态
				this.progressVisible = true
				this.showResultPage = false
				this.progressPercent = 0
				this.resultDuration = '0秒'
				this.resultStepText = progressCfg.steps[0].text
				this._taskStartTime = Date.now()
				// 启动进度模拟
				this._stopProgressTimer()
				this._progressTimer = setInterval(() => {
					const elapsedMs = Date.now() - this._taskStartTime
					const elapsed = elapsedMs / 1000
					// 按工具配置的总时长到达 100%
					const totalSec = progressCfg.totalSec
					const ratio = Math.min(elapsed / totalSec, 1)
					const eased = 1 - Math.pow(1 - ratio, 2) // easeOutQuad
					const timerValue = Math.floor(eased * 100)
					// 保证只增不减：轮询 status=5 可能额外加过 8%，不能用纯时间值覆盖
					this.progressPercent = Math.max(timerValue, this.progressPercent)
					// 实时更新顶部耗时
					const sec = Math.floor(elapsed)
					if (sec < 60) {
						this.resultDuration = sec + '秒'
					} else {
						const min = Math.floor(sec / 60)
						const remSec = sec % 60
						this.resultDuration = min + '分' + (remSec > 0 ? remSec + '秒' : '')
					}
					// 阶段文案切换（按工具配置）
					const steps = progressCfg.steps
					let matchedText = steps[steps.length - 1].text
					for (let i = 0; i < steps.length; i++) {
						if (elapsed < steps[i].untilSec) {
							matchedText = steps[i].text
							break
						}
					}
					this.resultStepText = matchedText
				}, 500)

				// 根据 tool 参数获取 taskId
				const tool = this.$route.query.tool || 'academic-qa'
				const taskId = this.$route.query.taskId || 0

				// 注入 tool_id，并清理辅助字段（_uploading、_name）
				const cleanFormData = {}
				Object.keys(formData).forEach(k => {
					if (!k.endsWith('_uploading') && !k.endsWith('_name')) {
						cleanFormData[k] = formData[k]
					}
				})
				// claritone-pubmed：自动生成 secondary_text 编码参数
				if (tool === 'claritone-pubmed') {
					const mode = cleanFormData.nnscholar_mode || 'ai'
					const max = cleanFormData.nnscholar_max || '30'
					const yearStart = cleanFormData.nnscholar_year_start || ''
					const yearEnd = cleanFormData.nnscholar_year_end || ''
					const years = (yearStart && yearEnd) ? yearStart + '-' + yearEnd : ''
					cleanFormData.secondary_text = 'mode=' + mode + '; max=' + max + '; years=' + years
				}
				cleanFormData.tool_id = this.toolIdMap[tool] || ''

				// 构造请求体（匹配 /api/task/create 接口）
				const requestBody = {
					userId: 0,
					taskId: taskId,
					params: JSON.stringify(cleanFormData),
					accessKey: localStorage.getItem('accessKey') || ''
				}

				this.$axios.post('/api/task/create', requestBody)
					.then(res => {
						if (res.data && res.data.status && res.data.status == 200 && res.data.data) {
							const taskLogId = res.data.data;
							console.log("taskLogId:", taskLogId);
							// isSubmitting 保持 true，轮询结束后才设为 false
							this.startPolling(taskLogId)
						} else {
							this._resetSubmitState()
							const errMsg = (res.data && res.data.errMsg) || '提交失败'
							alert(errMsg)
							// 密钥相关错误：清除 key 并弹出重填
							if (errMsg.includes('密钥') || errMsg.includes('访问密钥')) {
								localStorage.removeItem('accessKey')
								this.showKeyModal = true
							}
						}
					})
					.catch(err => {
						this._resetSubmitState()
						alert('请求出错：' + (err.message || err))
					})
			},


			// 开启轮询
			startPolling(taskLogId) {
				this.stopPolling()
				this.pollTimer = setInterval(() => {
					this.$axios.post('/api/task/query', {
							taskLogId: taskLogId
						})
						.then(res => {
							if (!res.data || !res.data.status) {
								this.stopPolling()
								alert('获取任务信息失败，请稍修改再试');
								return
							}
							if (res.data.status != 200) {
								this.stopPolling()
								alert('获取任务信息失败，请稍修改再试');
								return
							}
							const log = res.data.data
							if (!log) return
							const status = log.status
							// 4=待处理，5=处理中，6=已完成，7=处理失败
							if (status === 6) {
								this.stopPolling()
								this._stopProgressTimer()
								this.isSubmitting = false
								// 更新结果卡片为完成状态
								this.progressPercent = 100
								this.resultStepText = '检索完成'
								if (this._taskStartTime) {
									const sec = Math.round((Date.now() - this._taskStartTime) / 1000)
									this.resultDuration = sec + '秒'
								}
								// 解析结果 JSON，在 ResultPage 中展示
								let resultObj = log.result
								if (typeof resultObj === 'string') {
									try { resultObj = JSON.parse(resultObj) } catch (e) {}
								}
								if (resultObj && typeof resultObj === 'object' && resultObj.markdown) {
									this.resultMarkdown = resultObj.markdown
									this.resultTitle = resultObj.title || '任务结果'
									this.resultToolName = resultObj.tool_id || ''
									this.resultElapsed = resultObj.elapsed_ms || 0
									this.resultDocxBase64 = resultObj.docx_base64 || ''
									this.resultDocxFilename = resultObj.docx_filename || (this.resultTitle + '.docx')
									this.resultCopyText = resultObj.copy_text || resultObj.markdown || ''
							this.resultSummary = resultObj.summary || {}
								} else if (typeof resultObj === 'string') {
									this.resultMarkdown = resultObj
									this.resultDocxBase64 = ''
									this.resultCopyText = resultObj
								} else if (log.result && typeof log.result === 'string') {
									this.resultMarkdown = log.result
									this.resultDocxBase64 = ''
									this.resultCopyText = log.result
								} else {
									this.resultMarkdown = '无结果内容'
									this.resultDocxBase64 = ''
									this.resultCopyText = ''
								}
								// 切换到结果页（隐藏进度卡 + 显示 ResultPage）
								this.progressVisible = false
								this.showResultPage = true
							}							 else if (status === 7) {
								this.stopPolling()
								this._stopProgressTimer()
								this.isSubmitting = false
								// 解析错误详情
								let errDetail = log.result || '未知错误'
								if (typeof errDetail === 'string') {
									try {
										const errObj = JSON.parse(errDetail)
										if (errObj.detail) errDetail = errObj.detail
									} catch (e) {}
								}
								this.resultErrorDetail = errDetail
								this.resultTitle = '任务处理失败'
								this.resultMarkdown = ''  // 错误时清空 markdown，只显示 errorMessage
								this.progressVisible = false
								this.showResultPage = true} else if (status === 5) {
								// 处理中 — 渐进进度
								if (this.progressPercent < 90) {
									this.progressPercent = Math.min(90, this.progressPercent + 8)
								}
							} else if (status === 4) {
								// 待处理
								this.resultStepText = '等待处理'
							}
						})
						.catch(err => {
							this.stopPolling()
							this.isSubmitting = false
							alert('轮询出错：' + (err.message || err))
						})
				}, 10000)
			},

			// 停止轮询
			stopPolling() {
				if (this.pollTimer) {
					clearInterval(this.pollTimer)
					this.pollTimer = null
				}
			},

			// 停止进度计时器
			_stopProgressTimer() {
				if (this._progressTimer) {
					clearInterval(this._progressTimer)
					this._progressTimer = null
				}
			},
			// 统一重置提交状态（用于创建任务/轮询/密钥重填等多种失败场景）
			_resetSubmitState() {
				this.stopPolling()
				this._stopProgressTimer()
				this.isSubmitting = false
				this.progressVisible = false
				this.showResultPage = false
				this.progressPercent = 0
				this.resultDuration = '0秒'
				this.resultStepText = '待处理'
			},

			handleFileUpload(key, event) {
				const file = event.target.files[0]
				if (!file) return

				// 显示上传状态
				this.$set(this.formData, key + '_uploading', true)
				this.$set(this.formData, key + '_name', file.name)

				const formData = new FormData()
				formData.append('file', file)

				this.$axios.post('/api/upload/file', formData, {
					headers: { 'Content-Type': 'multipart/form-data' }
				}).then(res => {
					if (res.data && res.data.status && res.data.status == 200 && res.data.data) {
						const uploadResp = res.data.data
						// 存储 downloadUrl，提交时会作为 file 参数传出
						this.$set(this.formData, key, uploadResp.downloadUrl || '')
						this.$set(this.formData, key + '_uploading', false)
						console.log('文件上传成功, downloadUrl:', uploadResp.downloadUrl)
					} else {
						const errMsg = (res.data && res.data.errMsg) || '上传失败'
						alert(errMsg)
						this.$set(this.formData, key + '_uploading', false)
					}
				}).catch(err => {
					alert('文件上传请求出错：' + (err.message || err))
					this.$set(this.formData, key + '_uploading', false)
				})
			},

			// ========== 对话式布局方法 ==========

			startNewChat() {
				this.conversations.unshift({
					title: '新的对话',
					time: '刚刚',
					messages: []
				})
				this.currentConvIndex = 0
				this.messages = []
				// 重置结果页与进度卡
				this.showResultPage = false
				this.progressVisible = false
				this.progressPercent = 0
				this.resultDuration = ''
				this.resultStepText = ''
			},

			// ========== 学术绘图（academic-drawing）客户端渲染 ==========
			// 该工具完全在浏览器端执行，调用 window.ScientificCharts.buildSmartChart
			// 不走后端 API，不轮询任务状态

			async handleAcademicDrawing(formData) {
				// 显示进度条过渡（仅 1-2 秒，客户端渲染快）
				this.isSubmitting = true
				this.progressVisible = true
				this.showResultPage = false
				this.progressPercent = 0
				this.resultDuration = '0秒'
				this.resultStepText = '正在解析粘贴数据'
				this._taskStartTime = Date.now()
				this._chartStartTime = Date.now()
				this._stopProgressTimer()
				const progressCfg = this.progressConfigMap['academic-drawing']
				this._progressTimer = setInterval(() => {
					const elapsed = (Date.now() - this._taskStartTime) / 1000
					const totalSec = progressCfg.totalSec
					const ratio = Math.min(elapsed / totalSec, 1)
					const eased = 1 - Math.pow(1 - ratio, 2)
					this.progressPercent = Math.floor(eased * 100)
					const sec = Math.floor(elapsed)
					this.resultDuration = sec + '秒'
					const steps = progressCfg.steps
					let matchedText = steps[steps.length - 1].text
					for (let i = 0; i < steps.length; i++) {
						if (elapsed < steps[i].untilSec) {
							matchedText = steps[i].text
							break
						}
					}
					this.resultStepText = matchedText
				}, 200)

				// 缓存当前 formData，用于重新绘图
				this._lastChartFormData = formData

				// 客户端渲染（buildSmartChart 实际只需几百毫秒）
				this.$nextTick(async () => {
					try {
						if (typeof window.ScientificCharts === 'undefined' || typeof window.ScientificCharts.buildSmartChart !== 'function') {
							throw new Error('学术绘图模块未加载，请刷新页面重试')
						}
						// 构造 FormData（buildSmartChart 要求 FormData 对象）
						const fd = new FormData()
						// formData 可能是普通对象，转为 FormData
						Object.keys(formData).forEach(k => {
							if (k.endsWith('_uploading') || k.endsWith('_name')) return
							const v = formData[k]
							if (v !== null && v !== undefined) {
								fd.append(k, v)
							}
						})
						// 必填字段兜底
						if (!fd.has('primary_text')) {
							fd.set('primary_text', fd.get('data') || fd.get('text') || '')
						}
						if (!fd.has('chart_type')) {
							fd.set('chart_type', 'auto')
						}

						const result = await window.ScientificCharts.buildSmartChart(fd)
						if (!result || !result.chart) {
							throw new Error('图表生成失败：返回数据为空')
						}
						const chart = result.chart
						const html = window.ScientificCharts.renderChartResult(chart)
						this.chartResult = { chart, html, title: chart.title || '模型性能及95%置信区间' }
						this.chartElapsedMs = Date.now() - (this._chartStartTime || Date.now())
						this.chartError = ''
						// 绑定结果区按钮事件（renderChartResult 输出的是 HTML 字符串）
						this.bindChartResultButtons()
					} catch (e) {
						console.error('[academic-drawing] 客户端渲染失败：', e)
						this.chartResult = null
						this.chartError = e.message || String(e)
					} finally {
						// 关闭进度条
						this._stopProgressTimer()
						this.progressVisible = false
						this.progressPercent = 0
						this.isSubmitting = false
					}
				})
			},

			// 给 renderChartResult 输出的 HTML 绑定按钮事件
			bindChartResultButtons() {
				this.$nextTick(() => {
					// renderChartResult 内部按钮 ID：chart-download-svg / chart-download-png / chart-new-button
					const root = this.$refs.chartResultBody
					if (!root) return
					// renderChartResult 输出只有 #chart-download-png 和 #chart-new-button 两个按钮
					// 「保存 SVG」是 <a href download> 链接，浏览器原生支持，不需要 JS 事件
					const pngBtn = root.querySelector('#chart-download-png')
					const newBtn = root.querySelector('#chart-new-button')
					if (pngBtn && this.chartResult && this.chartResult.chart && typeof window.ScientificCharts.downloadChartPng === 'function') {
						pngBtn.addEventListener('click', () => window.ScientificCharts.downloadChartPng(this.chartResult.chart))
					}
					if (newBtn) {
						newBtn.addEventListener('click', () => this.clearChartResult())
					}
				})
			},

			// 重新绘图：复用上次表单数据
			regenerateChart() {
				if (!this._lastChartFormData) return
				// handleAcademicDrawing 是 async, 自动返回 Promise
				this.handleAcademicDrawing(this._lastChartFormData)
			},

			// 保存图表为 SVG
			saveChartSVG() {
				if (!this.chartResult || !this.chartResult.html) return
				const svgEl = this.$refs.chartResultBody?.querySelector('svg')
				if (!svgEl) return
				const svgData = new XMLSerializer().serializeToString(svgEl)
				const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
				const url = URL.createObjectURL(blob)
				const a = document.createElement('a')
				a.href = url
				a.download = 'chart_' + Date.now() + '.svg'
				document.body.appendChild(a)
				a.click()
				document.body.removeChild(a)
				URL.revokeObjectURL(url)
			},

			// 保存图表为 PNG
			saveChartPNG() {
				if (!this.chartResult || !this.chartResult.html) return
				const svgEl = this.$refs.chartResultBody?.querySelector('svg')
				if (!svgEl) return
				const svgData = new XMLSerializer().serializeToString(svgEl)
				const canvas = document.createElement('canvas')
				const ctx = canvas.getContext('2d')
				const img = new Image()
				const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
				const url = URL.createObjectURL(blob)
				img.onload = () => {
					canvas.width = img.naturalWidth
					canvas.height = img.naturalHeight
					ctx.drawImage(img, 0, 0)
					canvas.toBlob((pngBlob) => {
						const pngUrl = URL.createObjectURL(pngBlob)
						const a = document.createElement('a')
						a.href = pngUrl
						a.download = 'chart_' + Date.now() + '.png'
						document.body.appendChild(a)
						a.click()
						document.body.removeChild(a)
						URL.revokeObjectURL(pngUrl)
					})
					URL.revokeObjectURL(url)
				}
				img.src = url
			},

			// 新建图表：清空结果
			clearChartResult() {
				this.chartResult = null
				this.chartElapsedMs = 0
				this.chartError = ''
				this._lastChartFormData = null
			},

			// 格式化耗时显示
			formatDuration(ms) {
				if (ms < 1000) return ms + '毫秒'
				if (ms < 60000) return (ms / 1000).toFixed(1) + '秒'
				const m = Math.floor(ms / 60000)
				const s = Math.floor((ms % 60000) / 1000)
				return m + '分' + s + '秒'
			},

			switchConversation(index) {
				this.currentConvIndex = index
				this.messages = [...this.conversations[index].messages]
				this.sidebarOpen = false
			},

			sendMessage() {
				if (!this.userInput.trim() || this.isLoading) return

				const userMessage = {
					role: 'user',
					content: this.userInput
				}
				this.messages.push(userMessage)

				if (this.conversations[this.currentConvIndex]) {
					this.conversations[this.currentConvIndex].messages.push(userMessage)
					this.updateConversationTitle(this.userInput)
				}

				this.userInput = ''

				this.$nextTick(() => {
					this.scrollToBottom()
				})
			},

			sendQuickMessage(action) {
				this.userInput = action
				this.$nextTick(() => {
					this.sendMessage()
				})
			},

			updateConversationTitle(firstMessage) {
				if (this.conversations[this.currentConvIndex].title === '新的对话') {
					const title = firstMessage.substring(0, 20) + (firstMessage.length > 20 ? '...' : '')
					this.conversations[this.currentConvIndex].title = title
				}
			},

			scrollToBottom() {
				this.$nextTick(() => {
					const container = this.$refs.messagesContainer
					if (container) {
						container.scrollTop = container.scrollHeight
					}
				})
			},

			// ========== 学术自查方法 ==========

			downloadTemplate(format) {
				// 学术绘图 CSV 模板下载
				const currentToolId = this.$route.query.tool
				if (currentToolId === 'academic-drawing' && (format === 'csv' || format === 'excel')) {
					const t = this.currentTool
					const cols = t.templateTableColumns || []
					const rows = t.templateTableRows || []
					const csvLines = [cols.join(',')]
					rows.forEach(r => csvLines.push(r.join(',')))
					const csv = '\uFEFF' + csvLines.join('\n')
					const blob = new Blob([csv], {
						type: 'text/csv;charset=utf-8;'
					})
					const link = document.createElement('a')
					link.href = URL.createObjectURL(blob)
					link.download = '学术绘图模板.csv'
					link.click()
					URL.revokeObjectURL(link.href)
					return
				}
				const formatNames = {
					xlsx: 'Excel',
					csv: 'CSV',
					word: 'Word',
					txt: 'TXT'
				}
				const demoPaths = {
					word: '/demo/paper_humanizer_english_example.docx',
					txt: '/demo/论文降AI率英文正文示例模板.txt'
				}
				const url = demoPaths[format]
				if (url) {
					const a = document.createElement('a')
					a.href = url
					a.download = format === 'word' ? 'paper_humanizer_english_example.docx' : '论文降AI率英文正文示例模板.txt'
					a.style.display = 'none'
					document.body.appendChild(a)
					a.click()
					document.body.removeChild(a)
				} else {
					alert('正在下载 ' + formatNames[format] + ' 模板...')
				}
			},

			handleSelfcheckFileUpload(event) {
				const file = event.target.files[0]
				if (file) {
					this.selfcheckFileName = file.name
				}
			},

			submitSelfcheck() {
				this.isSubmitting = true
				setTimeout(() => {
					this.isSubmitting = false
					alert('学术自查提交成功！（功能开发中）')
				}, 1500)
			},

			// ========== 论文结构化解析方法 ==========

			handleStructureFileUpload(event) {
				const file = event.target.files[0]
				if (file) {
					this.structureFileName = file.name
				}
			},

			submitStructure() {
				this.isSubmitting = true
				setTimeout(() => {
					this.isSubmitting = false
					alert('论文结构化解析提交成功！（功能开发中）')
				}, 1500)
			},

		},
		beforeDestroy() {
			this.stopPolling()
		}
	}
</script>

<style scoped>
	.chat-page {
		width: 100%;
		min-height: 100vh;
		display: flex;
		flex-direction: column;
		background: #f5f7fa;
	}

	.chat-container {
		display: flex;
		flex: 1;
		overflow: visible;
	}

	.chat-main {
		flex: 1;
		display: flex;
		flex-direction: column;
		position: relative;
		overflow: hidden;
	}

	.form-layout {
		background: white;
		/* 表单页面不使用内嵌滚动：让外层浏览器滚动条接管 */
		overflow: visible;
	}
	/* 表单页面下取消 chat-main 的高度限制 */
	.chat-main.form-layout {
		overflow: visible;
	}
	/* 表单页面下取消 chat-container 的高度限制 */
	.chat-container:has(.form-layout) {
		overflow: visible;
	}

	.sidebar-toggle {
		display: none;
		position: absolute;
		top: 16px;
		left: 16px;
		background: white;
		border: 1px solid #e8e8e8;
		padding: 8px 12px;
		border-radius: 8px;
		cursor: pointer;
		font-size: 18px;
		z-index: 10;
	}

	.sidebar-overlay {
		display: none;
	}

	@media (max-width: 768px) {
		.sidebar-toggle {
			display: block;
		}

		.sidebar-overlay {
			display: block;
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background: rgba(0, 0, 0, 0.5);
			z-index: 199;
		}
	}

	/* Key 验证弹窗 */
	.key-modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: rgba(0, 0, 0, 0.5);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1000;
	}

	.key-modal {
		background: white;
		border-radius: 12px;
		padding: 32px;
		width: 400px;
		max-width: 90%;
		box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
	}

	.key-modal-header {
		margin-bottom: 20px;
	}

	.key-modal-header h3 {
		margin: 0 0 8px;
		font-size: 18px;
		color: #1a2b3a;
	}

	.key-modal-desc {
		margin: 0;
		font-size: 13px;
		color: #888;
	}

	.key-modal-body {
		margin-bottom: 20px;
	}

	.key-input {
		width: 100%;
		padding: 10px 14px;
		border: 1px solid #d9d9d9;
		border-radius: 8px;
		font-size: 14px;
		outline: none;
		box-sizing: border-box;
		transition: border-color 0.2s;
	}

	.key-input:focus {
		border-color: #0077b6;
		box-shadow: 0 0 0 2px rgba(0, 119, 182, 0.1);
	}

	.key-modal-error {
		margin: 8px 0 0;
		font-size: 12px;
		color: #e74c3c;
	}

	.key-modal-footer {
		display: flex;
		justify-content: space-between;
	}

	.key-btn {
		padding: 8px 24px;
		border: none;
		border-radius: 8px;
		font-size: 14px;
		cursor: pointer;
		transition: opacity 0.2s;
	}

	.key-btn:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.key-btn-secondary {
		background: #f0f0f0;
		color: #666;
	}

	.key-btn-secondary:hover {
		background: #e0e0e0;
	}

	.key-btn-primary {
		background: #0077b6;
		color: white;
	}

	.key-btn-primary:hover:not(:disabled) {
		background: #005f94;
	}
/* 结果展示弹窗 CSS 已废弃：结果已改为在页面下方内嵌展示（ResultPage），弹窗相关样式已移除 */

/* ===== 任务生成中遮罩 ===== 已废弃：进度由 result-card 展示，相关样式已移除 */


/* ===== 任务生成中遮罩 ===== 已废弃：进度由 result-card 展示，相关样式已移除 */



/* ===== 学术绘图（academic-drawing）客户端结果区样式 ===== */
.chart-result-area {
	margin: 20px auto;
	max-width: 1200px;
	background: #f0f0f0;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	overflow: hidden;
}
/* 顶部标题栏 */
.chart-top-bar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 18px;
	background: #e8e8e8;
	border-bottom: 1px solid #d8d8d8;
}
.chart-top-bar-title {
	font-size: 14px;
	font-weight: 600;
	color: #1a1a1a;
}
.chart-top-bar-meta {
	font-size: 12.5px;
	color: #777;
}
/* 内容卡片 - 最大高度500px，超出滚动 */
.chart-content-card {
	margin: 0;
	padding: 20px 24px;
	background: #ffffff;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
	max-height: 500px;
	overflow-y: auto;
}
.chart-card-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 16px;
}
.chart-card-title {
	margin: 0;
	font-size: 15px;
	font-weight: 500;
	color: #1a1a1a;
}
.chart-card-actions {
	display: flex;
	gap: 10px;
}
.chart-action-btn {
	padding: 6px 14px;
	font-size: 12px;
	border-radius: 4px;
	cursor: pointer;
	border: 1px solid #d0d0d0;
	background: #ffffff;
	color: #555;
	transition: all 0.2s;
}
.chart-action-btn:hover {
	background: #f5f5f5;
	border-color: #b0b0b0;
}
.chart-action-btn.primary {
	background: #fff5f5;
	border-color: #ffcccc;
	color: #c44569;
}
.chart-action-btn.primary:hover {
	background: #ffe0e0;
}
/* 绘图区域 - 不自带滚动，由外层 .chart-content-card 控制 */
.chart-result-body {
	padding-right: 8px;
}
.chart-error-card {
	margin: 24px auto;
	max-width: 1200px;
	padding: 24px;
	background: #fef5f5;
	border: 1px solid #f5c6cb;
	border-radius: 8px;
	text-align: center;
}
.chart-error-title {
	font-size: 16px;
	font-weight: 600;
	color: #c0392b;
	margin-bottom: 8px;
}
.chart-error-detail {
	font-size: 13px;
	color: #666;
	margin-bottom: 16px;
	word-break: break-all;
}
</style>